import React, { useEffect, useState } from "react";
import { Checkbox } from "@progress/kendo-react-inputs";
import type { UserCountry } from "@/app/types/adminMenu";
import { t } from "i18next";

export interface CountrySupportSelectorProps {
  countryList: UserCountry[];
  onToggle: (smeCountryId: number) => void;
  loading?: boolean;
  label?: string;
  className?: string;
  disabled?: boolean;
  triggerValidation?: boolean;
  validationMessage?: string;
  onValidate?: (field: string, isValid: boolean) => void;
  fieldName?: string;
}

const CountrySupportSelector: React.FC<CountrySupportSelectorProps> = ({
  countryList,
  onToggle,
  loading = false,
  label = "Where do you do business?",
  className = "",
  disabled = false,
  triggerValidation = false,
  validationMessage,
  onValidate,
  fieldName = "countrySupportSelector",
}) => {
  const [touched, setTouched] = useState(false);
  const [localValidationMessage, setLocalValidationMessage] = useState("");

  const validateCountrySelection = (): string => {
    const hasAnySelected = countryList.some((c) => c?.checkBox === "Y");
    if (!hasAnySelected) {
      return t("please_select_at_least_one_country");
    }
    return "";
  };

  useEffect(() => {
    if (triggerValidation) {
      const msg = validateCountrySelection();
      setLocalValidationMessage(msg);
      onValidate?.(fieldName, !msg);

      if (msg) {
        setTouched(true);
      }
    } else {
      setTouched(false);
      setLocalValidationMessage("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerValidation]);

  useEffect(() => {
    if (touched || triggerValidation) {
      const msg = validateCountrySelection();
      setLocalValidationMessage(msg);
      onValidate?.(fieldName, !msg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryList]);

  const handleToggle = (smeCountryId: number) => {
    onToggle(smeCountryId);

    if (touched || triggerValidation) {
      setTouched(true);
    }
  };

  const displayError = validationMessage || localValidationMessage;
  const isInvalid = !!displayError && (touched || triggerValidation);

  if (loading) {
    return (
      <div className={`gx-country-support-loading ${className}`}>
        <div className="gx-country-support-loading-title" />

        <div className="gx-country-support-loading-items">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="gx-country-support-loading-item"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <p className="gx-p14-b gx-country-support-label">
        {label}
      </p>

      <div
        className={`gx-country-support-list ${
          isInvalid ? "gx-country-support-invalid" : ""
        }`}
      >
        {countryList.map((country) => {
          const isChecked = country?.checkBox === "Y" || disabled;

          return (
            <div
              key={country.smeCountryId}
              className="gx-country-support-item"
            >
              <Checkbox
                checked={isChecked}
                onChange={() => handleToggle(country.smeCountryId)}
                disabled={disabled}
              />

              <span className="gx-p14-m gx-country-support-country-name">
                {country.countryName}
              </span>
            </div>
          );
        })}

        {countryList.length === 0 && (
          <p className="gx-p14-m gx-country-support-empty">
            No countries available.
          </p>
        )}
      </div>

      {isInvalid && (
        <div className="gx_input_validationmessage">
          {displayError}
        </div>
      )}
    </div>
  );
};

export default CountrySupportSelector;
